import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/VerifyEmail.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const email = searchParams.get('email') || '';

  const [verificationCode, setVerificationCode] = useState('');
  const [emailInput, setEmailInput] = useState(email);
  const [errors, setErrors] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (email) {
      setEmailInput(email);
    }
  }, [email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'code') {
      setVerificationCode(value);
    } else if (name === 'email') {
      setEmailInput(value);
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!emailInput) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!verificationCode) {
      newErrors.code = 'Verification code is required';
    } else if (!/^\d{6}$/.test(verificationCode)) {
      newErrors.code = 'Verification code must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsVerifying(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-email`, {
        email: emailInput,
        verification_code: verificationCode,
      }, {
        withCredentials: true,
      });

      if (response.data.success) {
        // Refresh auth state
        await checkAuth();
        // Redirect to dashboard (subscription is automatically active after email verification)
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Verification error:', error);
      setErrors({
        submit: error.response?.data?.error || 'Verification failed. Please try again.',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <h1>Verify Your Email</h1>
        <p className="instructions">
          We've sent a 6-digit verification code to your email address.
          Please enter it below to continue.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={emailInput}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className={errors.email ? 'error' : ''}
              disabled={!!email}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              name="code"
              value={verificationCode}
              onChange={handleChange}
              placeholder="000000"
              maxLength="6"
              className={errors.code ? 'error' : ''}
              pattern="[0-9]{6}"
            />
            {errors.code && <span className="error-message">{errors.code}</span>}
          </div>

          {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

          <button type="submit" className="verify-button" disabled={isVerifying}>
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default VerifyEmail;
